/**
 * #1071 — the switch for the highest-volume initial spread/total line.
 *
 *   VITE_INITIAL_LINE_VOLUME   `on` | `1` | `true` ⇒ the board starts each
 *                              game on the busiest line of its 50¢ window,
 *                              and Game Detail opens on the board's line
 *                              (`docs/261007Issue1071HighestVolumeInitialLine.md`).
 *                              Anything else ⇒ OFF: today's closest-to-50¢
 *                              defaults, unchanged. While OFF the board still
 *                              counts what the rule WOULD have chosen
 *                              (`initialLineTally.ts`), so the count can be
 *                              read before anyone switches it on.
 *
 * RUNTIME OVERRIDE (per browser, no rebuild), the feed-filter pattern
 * (`src/clients/feedFilterConfig.ts`): `?initialLineVolume=on|off` persists to
 * localStorage (`g1xl.initialLineVolume`) and wins over the env in both
 * directions; `=env` clears it. Read when the board or Game Detail's
 * Spread / Total section mounts, so a change applies from the next visit;
 * reload for a count that is all one mode.
 */

const OVERRIDE_KEY = 'g1xl.initialLineVolume';
const QUERY_KEY = 'initialLineVolume';

/** How long a row whose window volume has not all arrived shows the 50¢ line
 *  unstored before that line is seeded. The scope update asks for "a few
 *  seconds, set from measured arrival times"; nothing has been measured yet
 *  (no live per-line volume was observable when this shipped), so this is
 *  the one setting to tune once it is. */
export const INITIAL_LINE_WAIT_MS = 3_000;

function envOn(raw: string | undefined): boolean {
  const v = raw?.trim().toLowerCase();
  return v === 'on' || v === '1' || v === 'true';
}

/** Fold a `?initialLineVolume=` URL param into localStorage. Safe without
 *  window/storage (tests, workers). */
export function applyInitialLineUrlOverride(search?: string): void {
  if (typeof window === 'undefined') return;
  try {
    const v = new URLSearchParams(search ?? window.location.search).get(QUERY_KEY)?.trim().toLowerCase();
    if (v == null) return;
    if (v === 'env' || v === '') localStorage.removeItem(OVERRIDE_KEY);
    else localStorage.setItem(OVERRIDE_KEY, envOn(v) ? 'on' : 'off');
  } catch { /* storage unavailable — env decides */ }
}

function override(): boolean | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const v = localStorage.getItem(OVERRIDE_KEY);
    return v == null ? null : v === 'on';
  } catch {
    return null;
  }
}

/** Default OFF: only an explicit `on` (override or env) enables the rule.
 *  Read at call time (not module load) so unit tests can `vi.stubEnv`. */
export function initialLineVolumeEnabled(): boolean {
  applyInitialLineUrlOverride();
  return initialLineVolumeSetting();
}

/** The same answer without folding `?initialLineVolume=` into storage: for a
 *  display that must not change the switch (G1 Health). */
export function initialLineVolumeSetting(): boolean {
  return override() ?? envOn(import.meta.env.VITE_INITIAL_LINE_VOLUME as string | undefined);
}
